/**
 * The king-admin pieces of the catalogue article: the »Uredi opis« button,
 * the inline edit form that replaces `#sec-opis`, and the »Zgodovina
 * urejanja« section with »Obnovi« per audit row. Rendered only when the
 * viewer is a king admin (DocsPage.probeAdmin); the routes gate again.
 */

import type { WikiAuditRow, WikiEntry } from '../../../shared/types/layerWiki.js';
import { formatLjubljanaTs } from '../../../shared/utils/ljubljanaTime.js';
import { docsCopy } from '../docsCopy.js';
import type { EditState } from '../docsEdit.js';
import { originText } from '../docsFacts.js';
import { esc, paragraphs } from '../docsHtml.js';

export function renderEditButton(key: string): string {
  return `<button type="button" class="docs-btn-link docs-edit-btn" data-docs-edit="${esc(key)}">${esc(docsCopy.edit.button)}</button>`;
}

/**
 * The admin's line under the text: »Uredi opis« and, beside it, whose words the text is
 * (Teo 2026-09-28: the »Besedilo« row left the facts card — "have it next to the uredi opis
 * thats viewable by admins only anyways").
 */
export function renderEditRow(entry: WikiEntry): string {
  const origin = originText(entry);
  const note = origin ? `<span class="docs-edit-origin">${esc(docsCopy.facts.origin)}: ${esc(origin)}</span>` : '';
  return `<div class="docs-edit-row">${renderEditButton(entry.key)}${note}</div>`;
}

/** The form takes the description's place (same `#sec-opis` anchor for the rail). */
export function renderEditForm(entry: WikiEntry, state: EditState): string {
  const c = docsCopy.edit;
  const d = state.draft;
  const slInvalid = state.error === c.errors.emptySl;
  return `
    <form id="sec-opis" class="docs-edit" data-docs-edit-form="${esc(entry.key)}" novalidate aria-labelledby="docs-edit-title">
      <div id="docs-edit-title" class="docs-edit-title">${esc(c.title)}</div>
      <p id="docs-edit-hint" class="docs-edit-hint">${esc(c.hint)}</p>
      <div class="docs-edit-field">
        <label for="docs-edit-sl">${esc(c.sl)}</label>
        <textarea id="docs-edit-sl" name="descriptionSl" rows="10" required maxlength="20000" aria-describedby="docs-edit-hint"${slInvalid ? ' aria-invalid="true"' : ''}>${esc(d.sl)}</textarea>
      </div>
      <div class="docs-edit-field">
        <label for="docs-edit-cs">${esc(c.cs)}</label>
        <textarea id="docs-edit-cs" name="descriptionCs" rows="6" maxlength="20000" aria-describedby="docs-edit-hint" lang="cs">${esc(d.cs)}</textarea>
      </div>
      <div class="docs-edit-field">
        <label for="docs-edit-note">${esc(c.note)}</label>
        <input id="docs-edit-note" name="note" type="text" maxlength="500" autocomplete="off" value="${esc(d.note)}">
      </div>
      ${state.error ? `<p class="docs-edit-error" role="alert">${esc(state.error)}</p>` : ''}
      <div class="docs-edit-actions">
        <button type="submit" class="docs-btn-primary"${state.busy ? ' disabled' : ''}>${esc(state.busy ? c.saving : c.save)}</button>
        <button type="button" class="docs-btn-link" data-docs-edit-cancel>${esc(c.cancel)}</button>
      </div>
    </form>`;
}

function renderHistoryRow(row: WikiAuditRow, busy: boolean): string {
  const c = docsCopy.edit;
  const who = row.changedBy.name ?? row.changedBy.email ?? row.changedBy.id;
  const bits = [
    `<span class="docs-history-when">${esc(formatLjubljanaTs(row.changedAt, 'datetime'))}</span>`,
    `<span class="docs-history-action">${esc(c.actions[row.action])}</span>`,
    `<span class="docs-history-who">${esc(who)}</span>`,
  ];
  if (row.note) bits.push(`<span class="docs-history-note">${esc(row.note)}</span>`);
  const before = row.before;
  const prev = before
    ? `<details class="docs-history-details">
          <summary>${esc(c.previous)}</summary>
          <div class="docs-history-prev">${paragraphs(before.descriptionSl)}${
            before.descriptionCs ? `<div class="docs-history-prev-cs" lang="cs">${paragraphs(before.descriptionCs)}</div>` : ''
          }</div>
        </details>`
    : '';
  const restore = before
    ? `<button type="button" class="docs-btn-link docs-history-restore" data-docs-restore="${esc(row.id)}"${busy ? ' disabled' : ''}>${esc(c.restore)}</button>`
    : '';
  return `<li class="docs-history-row"><div class="docs-history-line">${bits.join('<span class="docs-history-sep" aria-hidden="true"> · </span>')}${restore}</div>${prev}</li>`;
}

/** Nothing until the history of THIS entry has loaded (or failed). */
export function renderHistory(state: EditState, key: string): string {
  const c = docsCopy.edit;
  const h = state.history;
  if (!h || h.key !== key) return '';
  const body = h.failed
    ? `<p class="docs-history-empty">${esc(c.historyFailed)}</p>`
    : h.rows.length === 0
      ? `<p class="docs-history-empty">${esc(c.noHistory)}</p>`
      : `<ul class="docs-history-list" role="list">${h.rows.map((r) => renderHistoryRow(r, state.busy)).join('')}</ul>`;
  // A restore failure lands here (there is no form open to carry it).
  const error = state.editingKey === null && state.error ? `<p class="docs-edit-error" role="alert">${esc(state.error)}</p>` : '';
  return `
    <section id="sec-zgodovina" class="docs-section docs-history">
      <h2 class="docs-section-title">${esc(c.history)}</h2>
      ${error}
      ${body}
    </section>`;
}
